"use client";

import { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
  Alert,
  Box,
  Divider,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { MoveRight, Receipt, User } from "lucide-react";
import axios from "axios";
import { API_URL } from "../../../config";
import { getCookie } from "../../../function";
import { useNavigate } from "react-router-dom";

const Adjustment = () => {
  const [fromInvoiceId, setFromInvoiceId] = useState("");
  const [toInvoiceId, setToInvoiceId] = useState("");
  const [amountToAdjust, setAmountToAdjust] = useState("");
  const [remark, setRemark] = useState("");
  const [fromInvoiceData, setFromInvoiceData] = useState();
  const [toInvoiceData, setToInvoiceData] = useState();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const navigate = useNavigate();

  const fetchInvoiceData = async (invoiceId, setInvoiceData) => {
    if (!invoiceId) {
      setInvoiceData(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/articles/${invoiceId}`, {
        headers: {
          Authorization: getCookie("token"),
          "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
        },
      });

      let parsedData = JSON.parse(response.data);
      let result = JSON.parse(parsedData.body);
      console.log(result);
      if (result && result.success) {
        const article = result.data;
        setInvoiceData({
          ...article,
          remaningFees: parseFloat(article.remaningFees),
        });
        setError("");
      } else {
        setInvoiceData(null);
        setError("Invoice not found or data is empty.");
      }
    } catch (err) {
      setInvoiceData(null);
      setError("Error fetching invoice data. Please check the ID.");
      console.error("Error fetching invoice data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData(fromInvoiceId, setFromInvoiceData);
  }, [fromInvoiceId]);

  useEffect(() => {
    fetchInvoiceData(toInvoiceId, setToInvoiceData);
  }, [toInvoiceId]);

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    if (!fromInvoiceData || !toInvoiceData) {
      setError("Invoice data not loaded.");
      setLoading(false);
      return;
    }

    const amount = Number.parseFloat(amountToAdjust);

    console.log("Amount to adjust:", amount);
    console.log(
      "From Invoice Additional Amount:",
      fromInvoiceData.additionalAmountReceived
    );
    console.log("To Invoice Remaining Fees:", toInvoiceData.remaningFees);

    if (isNaN(amount) || amount <= 0) {
      setError("Amount to adjust must be a positive number.");
      setLoading(false);
      return;
    }

    if (amount > fromInvoiceData.additionalAmountReceived) {
      setError(
        "Amount to adjust cannot exceed the additional amount of the From Invoice."
      );
      setLoading(false);
      return;
    }

    if (fromInvoiceData.currency !== toInvoiceData.currency) {
      setError("Currencies of both invoices must be the same.");
      setLoading(false);
      return;
    }

    if (amount > toInvoiceData.remaningFees) {
      setError(
        "Amount to adjust cannot exceed the remaining fees of the To Invoice."
      );
      setLoading(false);
      return;
    }

    try {
      const body = {
        fromInvoiceId: fromInvoiceId,
        toInvoiceId: toInvoiceId,
        amountToAdjust: amount,
        remark: remark || "",
      };
      const response = await axios.post(
        `${API_URL}/articles/adjustPayment`,
        body,
        {
          headers: {
            Authorization: getCookie("token"),
            "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
          },
        }
      );

      let parsedData = JSON.parse(response.data);
      let result = JSON.parse(parsedData.body);

      if (result.success) {
        setSuccess(result.message);
        setFromInvoiceId("");
        setToInvoiceId("");
        setAmountToAdjust("");
        setRemark("");
        setFromInvoiceData(null);
        setToInvoiceData(null);
        navigate(`/client/article/${toInvoiceId}`);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Error submitting adjustment.");
      console.error("Error submitting adjustment:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError("");
    setSuccess("");
  };

  const handleOpenConfirmDialog = (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (
      !fromInvoiceId ||
      !toInvoiceId ||
      !amountToAdjust ||
      !fromInvoiceData ||
      !toInvoiceData
    ) {
      setError(
        "Please fill in all required fields and ensure invoice data is fetched."
      );
      return;
    }

    const amount = Number.parseFloat(amountToAdjust);
    if (isNaN(amount) || amount <= 0) {
      setError("Amount to adjust must be a positive number.");
      return;
    }

    if (amount > fromInvoiceData.additionalAmountReceived) {
      setError(
        "Amount to adjust cannot exceed the additional amount of the From Invoice."
      );
      return;
    }

    if (fromInvoiceData.currency !== toInvoiceData.currency) {
      setError("Currencies of both invoices must be the same.");
      return;
    }

    if (amount > toInvoiceData.remaningFees) {
      setError(
        "Amount to adjust cannot exceed the remaining fees of the To Invoice."
      );
      return;
    }

    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmSubmit = () => {
    setOpenConfirmDialog(false);
    handleSubmit();
  };

  const InvoiceCard = ({ invoiceData, title }) => (
    <Card
      variant="outlined"
      sx={{
        mt: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 2,
        },
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Receipt size={18} />
            <Typography variant="subtitle1" fontWeight="600">
              {title}: {invoiceData.id}
            </Typography>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <Divider />
      <CardContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  {title === "Source" ? "Additional Amount" : "Remaining Fees"}
                </Typography>
              </Box>
              <Chip
                label={
                  title === "Source"
                    ? invoiceData.additionalAmountReceived !== undefined
                      ? String(invoiceData.additionalAmountReceived)
                      : "N/A"
                    : invoiceData.remaningFees !== undefined
                    ? String(invoiceData.remaningFees)
                    : "N/A"
                }
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Total Fees
                </Typography>
              </Box>
              <Chip
                label={String(invoiceData.totalfees)}
                color="secondary"
                variant="outlined"
                size="small"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Currency
                </Typography>
              </Box>
              <Chip
                label={invoiceData.currency}
                color="info"
                variant="outlined"
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <User size={16} color="#666" />
              <Typography variant="body2" color="text.secondary">
                Author Name
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight="500">
              {invoiceData.authorName}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        py: 4,
      }}
    >
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12} sm={11} md={10} lg={9} xl={8}>
          <Paper
            elevation={8}
            sx={{
              p: 4,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                fontWeight="700"
                color="primary"
              >
                Invoice Adjustment
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Transfer amounts between invoices with ease
              </Typography>
            </Box>

            <form onSubmit={handleOpenConfirmDialog}>
              <Grid container spacing={3}>
                {/* From Invoice Section */}
                <Grid item xs={12} md={5}>
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      color="text.primary"
                      fontWeight="600"
                    >
                      From Invoice
                    </Typography>
                    <TextField
                      fullWidth
                      label="From Invoice ID"
                      variant="outlined"
                      value={fromInvoiceId}
                      onChange={(e) => setFromInvoiceId(e.target.value)}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    {fromInvoiceData && (
                      <InvoiceCard
                        invoiceData={fromInvoiceData}
                        title="Source"
                      />
                    )}
                  </Box>
                </Grid>

                {/* Arrow Section */}
                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: { xs: 2, md: 4 },
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: 2,
                      transform: {
                        xs: "rotate(90deg)",
                        md: "rotate(0deg)",
                      },
                      transition: "transform 0.3s ease-in-out",
                    }}
                  >
                    <MoveRight size={24} />
                  </Box>
                </Grid>

                {/* To Invoice Section */}
                <Grid item xs={12} md={5}>
                  <Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      color="text.primary"
                      fontWeight="600"
                    >
                      To Invoice
                    </Typography>
                    <TextField
                      fullWidth
                      label="To Invoice ID"
                      variant="outlined"
                      value={toInvoiceId}
                      onChange={(e) => setToInvoiceId(e.target.value)}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    {toInvoiceData && (
                      <InvoiceCard
                        invoiceData={toInvoiceData}
                        title="Destination"
                      />
                    )}
                  </Box>
                </Grid>

                {/* Amount and Remark Section */}
                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Amount to Adjust"
                    variant="outlined"
                    type="number"
                    value={amountToAdjust}
                    onChange={(e) => setAmountToAdjust(e.target.value)}
                    required
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Remark (Optional)"
                    variant="outlined"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      boxShadow: 3,
                      "&:hover": {
                        boxShadow: 6,
                      },
                    }}
                  >
                    {loading ? "Processing..." : "Submit Adjustment"}
                  </Button>
                </Grid>
              </Grid>
            </form>

            <Snackbar
              open={!!error || !!success}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={error ? "error" : "success"}
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  fontWeight: 500,
                }}
                variant="filled"
              >
                {error || success}
              </Alert>
            </Snackbar>

            <Dialog
              open={openConfirmDialog}
              onClose={handleCloseConfirmDialog}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {"Verify Adjustment Details"}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Please verify the details before submitting:
                  <br />
                  <br />
                  From Invoice ID: <b>{fromInvoiceId}</b>
                  <br />
                  To Invoice ID: <b>{toInvoiceId}</b>
                  <br />
                  Amount to Adjust: <b>{amountToAdjust}</b>
                  <br />
                  <br />
                  Do you want to proceed with this adjustment?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseConfirmDialog} color="primary">
                  No, Cancel
                </Button>
                <Button onClick={handleConfirmSubmit} color="primary" autoFocus>
                  Yes, Adjust
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Adjustment;
